import {
  ClockIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Typography,
} from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputDate } from '@/components/FormFields/InputDate';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { StateForm } from '@/components/FormFields/StateForm';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchSegmentOcr } from '@/features/segments/api/patch-segment-ocr';
import { usePostSegmentOcr } from '@/features/segments/api/post-segment-ocr';
import { TPostSegmentOcr } from '@/features/segments/types';
import { getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

import { ROTULO_FORM_DEFAULT_VALUES } from '../constants';
import { TSegmentOcrForm } from '../types';

export function BroadcastScheduleProgramBodySegmentToolkitRotuloForm() {
  const {
    activeSegment,
    rotuloData,
    playerVideoCurrentTime,
    formBox,
    setRotuloData,
    setFormBox,
    drawnFormReset,
    setActiveSegment,
    setSegmentItems,
    handleDateTimeSegment,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const postSegmentOcr = usePostSegmentOcr();
  const patchSegmentOcr = usePatchSegmentOcr();

  const form = useForm<TSegmentOcrForm>({
    mode: 'all',
    defaultValues: ROTULO_FORM_DEFAULT_VALUES,
  });

  const handleSubmit = async (data: TSegmentOcrForm) => {
    const formatedData: TPostSegmentOcr = {
      ...data,
      segment_id: activeSegment?.id || '',
    };
    if (rotuloData?.id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostSegmentOcr) => {
    try {
      const item = await postSegmentOcr.mutateAsync(formatedData);
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_ocrs: [...(prev.segment_ocrs || []), item],
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_ocrs: [...(segment.segment_ocrs || []), item],
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el logo:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostSegmentOcr) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { segment_id, ...restData } = formatedData;
    try {
      const item = await patchSegmentOcr.mutateAsync({
        id: rotuloData?.id || '',
        body: restData,
      });
      setActiveSegment(prev => {
        if (prev) {
          return {
            ...prev,
            segment_ocrs: prev.segment_ocrs?.map(logo => {
              if (logo.id === item.id) {
                return item;
              }
              return logo;
            }),
          };
        }
        return prev;
      });
      setSegmentItems(prev =>
        prev.map(segment => {
          if (segment.id === formatedData?.segment_id) {
            return {
              ...segment,
              segment_ocrs: (segment.segment_ocrs || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return segment;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar el logo:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(ROTULO_FORM_DEFAULT_VALUES);
    setRotuloData(null);
    drawnFormReset();
  }, [setFormBox, setRotuloData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }

      if (event.ctrlKey && event.key === 'F2') {
        event.preventDefault();

        if (!activeSegment?.start_datetime) return;

        const newDateTime = getAbsoluteDateFromPlayerTime(
          activeSegment.start_datetime,
          playerVideoCurrentTime
        );

        form.setValue('start_datetime', newDateTime);
      }

      if (event.ctrlKey && event.key === 'F9') {
        event.preventDefault();

        if (!activeSegment?.start_datetime) return;

        const newDateTime = getAbsoluteDateFromPlayerTime(
          activeSegment.start_datetime,
          playerVideoCurrentTime
        );

        form.setValue('end_datetime', newDateTime);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSegment, form]);

  useEffect(() => {
    if (formBox !== 'rotulo') return;

    if (rotuloData?.id) {
      form.setValue('start_datetime', new Date(rotuloData.start_datetime));
      form.setValue('end_datetime', new Date(rotuloData.end_datetime || rotuloData.start_datetime));
      form.setValue('review_state', rotuloData.review_state);
      form.setValue('text', rotuloData.text || '');
    }
  }, [rotuloData]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
        <Typography
          fontSize={FontSizeEnum.Xl}
          color={FontColorEnum.Dark10}
          weight={FontWeightEnum.Medium}
        >
          {`${rotuloData ? t('Editar rótulo') : t('Crear nuevo rótulo')}`}
        </Typography>

        <IconButton
          size={IconButtonSizeEnum.LG}
          color={IconButtonColorEnum.NONE_DEFAULT}
          onClick={handleClickExit}
        >
          <CloseIcon size={IconSizeEnum.LG} />
        </IconButton>
      </div>
      <InputArea form={form} name="text" required label={t('Rótulo')} bigSize />
      <RowContainer>
        <InputDate
          form={form}
          name="start_datetime"
          required
          label={t('Inicio')}
          seconds
          help
          tooltipHelp="Rellenar"
          tooltipKeyword="F2"
          tooltipModifiers={['Ctrl']}
          iconTooltip={ClockIcon}
          onClickTooltip={() => handleDateTimeSegment(form, 'start_datetime')}
        />
        <InputDate
          form={form}
          name="end_datetime"
          required
          label={t('Fin')}
          seconds
          help
          tooltipHelp="Rellenar"
          tooltipKeyword="F9"
          tooltipModifiers={['Ctrl']}
          iconTooltip={ClockIcon}
          onClickTooltip={() => handleDateTimeSegment(form, 'end_datetime')}
        />
      </RowContainer>

      <StateForm
        className="!justify-start"
        form={form}
        name="review_state"
        activedName="Marcar como revisado"
        desactivedName="No revisado"
      />

      <FooterFormActions form={form} handleCancel={handleClickExit} handleSubmit={handleSubmit} />
    </div>
  );
}
